/* eslint-disable @angular-eslint/component-selector */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Signal,
  signal
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { map, Observable, timer } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  switchMap,
  takeWhile,
  tap,
  take
} from 'rxjs/operators';
import { AutoCompleteModel } from '../../models';
import { SearchService } from '../../services/search.service';
import { AutoCompleteTypeEnum } from '../../models/auto-complete-type.enum';

// autocomplete

@Component({
  selector: 'cpd-auto-complete[autoCompleteType]',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.scss'],
  providers: [
    SearchService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoCompleteComponent),
      multi: true
    }
  ]
})
export class AutoCompleteComponent
  implements ControlValueAccessor, OnInit, OnDestroy, OnChanges
{
  @Input() public autoCompleteType: AutoCompleteTypeEnum;
  @Input() public apiUrl: string;
  @Input() public label: string;
  @Input() public shouldLabelFloat: boolean;
  @Input() public placeholder: string;
  @Input() public isRequired: boolean;
  @Input() public shouldUseProductionAPI: boolean;
  @Input() public errorMessage: string;
  @Input() public formFieldAppearance: string;
  @Input() public displayNameFn: (a: AutoCompleteModel) => string;
  @Input() public svgSearchIconName: string;
  @Input() public svgCloseIconName: string;
  @Input() public touched: boolean;
  @Input() public millisecondsToEmitWhenStoppedTyping: number;
  @Input() public debounce: number;
  @Input() public limit: number;
  private readonly internalShouldHaveFuzzyResults = signal(false);
  private externalShouldHaveFuzzyResults: Signal<boolean> | null = null;

  @Input()
  public set shouldHaveFuzzyResults(value: boolean | Signal<boolean>) {
    if (typeof value === 'function') {
      this.externalShouldHaveFuzzyResults = value;
    } else {
      this.externalShouldHaveFuzzyResults = null;
      this.internalShouldHaveFuzzyResults.set(value);
    }
  }

  public get shouldHaveFuzzyResults(): boolean {
    return (
      this.externalShouldHaveFuzzyResults ?? this.internalShouldHaveFuzzyResults
    )();
  }

  @Output() public clearAutoComplete = new EventEmitter<AutoCompleteModel>();
  @Output() public selection = new EventEmitter<AutoCompleteModel>();
  @Output() public keystroke = new EventEmitter<string>();
  @Output() public finishedTyping = new EventEmitter<void>();

  public selctedAutoCompleteModel: AutoCompleteModel;
  public filteredSearchList$: Observable<AutoCompleteModel[]>;
  public fuzzySearchList$: Observable<AutoCompleteModel[]>;
  public isFilteredSearchListEmpty$: Observable<boolean>;
  public hasFuzzyResults$: Observable<boolean>;
  public floatLabel = 'auto';

  private alive = true;
  private timeout: any = null;
  private pendingWriteValue: null | AutoCompleteModel = null;

  public formGroup: FormGroup;
  private onChangeValue: any = (_: AutoCompleteModel) => {};
  private onTouch: any = () => {};

  constructor(
    private formBuilder: FormBuilder,
    public searchService: SearchService
  ) {}

  public ngOnInit(): void {
    if (!this.displayNameFn) {
      this.displayNameFn = (autoCompleteModel: AutoCompleteModel) =>
        autoCompleteModel?.name;
    }
    if (this.autoCompleteType === null || this.autoCompleteType === undefined) {
      throw new TypeError("The 'autoCompleteType' input is required");
    } else if (
      this.autoCompleteType === AutoCompleteTypeEnum.Custom &&
      this.apiUrl.trim().length === 0
    ) {
      throw new Error(
        'apiUrl is required parameter for AutoCompleteTypeEnum.Custom autoCompleteType.'
      );
    }

    if (this.shouldUseProductionAPI === undefined) {
      this.shouldUseProductionAPI = true;
    }

    this.apiUrl =
      this.autoCompleteType === AutoCompleteTypeEnum.Custom
        ? this.apiUrl
        : this.shouldUseProductionAPI
          ? this.autoCompleteType.ApiUrlProd
          : this.autoCompleteType.ApiUrlDev;
    this.label = this.label || 'Enter Search Term';
    this.floatLabel = this.shouldLabelFloat === false ? 'always' : 'auto';
    this.placeholder = this.placeholder || 'Enter Search Term';
    this.formFieldAppearance = this.formFieldAppearance || 'fill';
    this.filteredSearchList$ = this.searchService.filteredResultsList$;
    this.fuzzySearchList$ = this.searchService.fuzzyResultsList$;
    this.isFilteredSearchListEmpty$ =
      this.searchService.filteredResultsList$.pipe(
        filter((data) => data !== null),
        map((data) => data.length === 0)
      );
    this.hasFuzzyResults$ = this.searchService.fuzzyResultsList$.pipe(
      map((data) => data.length > 0)
    );
    this.errorMessage =
      this.errorMessage || `${this.label.trim().toLowerCase()} is invalid.`;
    this.millisecondsToEmitWhenStoppedTyping =
      this.millisecondsToEmitWhenStoppedTyping || 1500;
    this.debounce = this.debounce || 300;

    this.buildForm();
  }

  public emitOptionSelected(searchResultModel: AutoCompleteModel): void {
    this.selection.emit(searchResultModel);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['touched'] && changes['touched'].currentValue) {
      this.formGroup.markAllAsTouched();
    }
  }

  public ngOnDestroy(): void {
    // the following destroys subscribed to value changes
    this.alive = false;
  }

  public displayFn(autoCompleteModel: AutoCompleteModel): string | undefined {
    return autoCompleteModel
      ? this.displayNameFn(autoCompleteModel)
      : undefined;
  }

  public displayLabel(
    searchResultModel: AutoCompleteModel,
    index?: number
  ): string | undefined {
    const indexMessage: string = index !== undefined ? `${index + 1}. ` : '';
    return searchResultModel
      ? `${indexMessage}${this.displayNameFn(searchResultModel)}`
      : undefined;
  }

  public displayCountFn(
    searchResultModel?: AutoCompleteModel
  ): string | undefined {
    return searchResultModel && searchResultModel.data['optionGroupCount']
      ? `${this.displayNameFn(searchResultModel)} (${
          searchResultModel.data['optionGroupCount']
        } occupations)`
      : this.displayNameFn(searchResultModel);
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selctedAutoCompleteModel = event.option.value;
    this.formGroup.controls.searchTerm.setValue(event.option.value);
    this.onChangeValue(event.option.value);
    this.onTouch();
    this.emitOptionSelected(event.option.value);
  }

  public clear(): void {
    const clearedAutoCompleteModel: AutoCompleteModel =
      this.selctedAutoCompleteModel;
    this.selctedAutoCompleteModel = null;
    this.formGroup.controls.searchTerm.setValue(null);
    this.searchService.clear();
    this.onChangeValue(null);

    this.clearAutoComplete.emit(clearedAutoCompleteModel);
    this.emitOptionSelected(undefined);
  }

  public writeValue(value: null | AutoCompleteModel): void {
    if (!this.formGroup) {
      this.pendingWriteValue = value;
      return;
    }

    this.selctedAutoCompleteModel = value;
    this.formGroup.controls.searchTerm.setValue(value);
  }

  public registerOnChange(fn: () => void): void {
    this.onChangeValue = fn;
  }

  public registerOnTouched(fn: (_: AutoCompleteModel) => void): void {
    this.onTouch = fn;
  }

  public onBlurEvent(event: any): void {
    this.onTouch();

    timer(300)
      .pipe(take(1))
      .subscribe(() => {
        if (event.target.value && !this.selctedAutoCompleteModel) {
          this.formGroup.controls.searchTerm.setErrors({ invalid: true });
        }
      });
  }

  private buildForm(): void {
    this.searchService.clear();

    this.formGroup = this.formBuilder.group({
      searchTerm: [],
      selectedAutoCompleteOption: []
    });

    if (this.isRequired) {
      this.formGroup.controls.searchTerm.setValidators([Validators.required]);
    }

    if (this.pendingWriteValue !== null) {
      this.selctedAutoCompleteModel = this.pendingWriteValue;
      this.formGroup.controls.searchTerm.setValue(this.pendingWriteValue);
      this.pendingWriteValue = null;
    }

    this.buildValueChanges();
  }

  private buildValueChanges(): void {
    // SEND SERVICE CALL FOR LOCATIONS BASED ON SEARCH TERM
    this.formGroup.controls.searchTerm.valueChanges
      .pipe(
        takeWhile(() => this.alive),
        tap((searchString: string) => {
          clearTimeout(this.timeout);
          this.setTimeOutToEmitWhenFinishedTyping();
          this.emitKeyStroke(searchString);
        }),
        debounceTime(this.debounce),
        startWith(''),
        filter((value) => {
          return (
            value !== '' &&
            typeof value !== 'object' &&
            value &&
            value.trim().length >= 2
          );
        }),
        distinctUntilChanged(),
        switchMap((searchString: string) =>
          this.searchService.searchGraph(this.autoCompleteType, searchString)
        )
      )
      .subscribe();
  }

  private emitKeyStroke(searchString: string): void {
    const isSearchStringOrNull =
      typeof searchString === 'string' || searchString === null;

    if (isSearchStringOrNull) {
      this.keystroke.emit(searchString);
    }
  }

  private setTimeOutToEmitWhenFinishedTyping(): void {
    timer(this.millisecondsToEmitWhenStoppedTyping)
      .pipe(take(1))
      .subscribe(() => {
        this.finishedTyping.emit();
      });
  }
}
